import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { getProjectRecommendations } from './helpers';
import styles from './ProjectDetails.module.scss';
import { PROJECT_ID_PARAMETER_NAME, PROJECT_STATUSES } from '../../constants';
import {
  customerHoverCardFields,
  employeeHoverCardFields,
} from '../../helpers';
import { useGetConfigurationFeatures } from '@/api/REST/configuration-features';
import { useGetProjectDetails } from '@/api/REST/projects';
import { useGetProjectById } from '@/api/typesense/projects';
import {
  GaugeRealized,
  GaugeRealizedLoading,
} from '@/components/charts/GaugeRealized';
import { EntityHoverCard } from '@/components/entity/EntityHoverCard';
import { Factor } from '@/components/ui/Factor';
import { Icon } from '@/components/ui/Icon';
import { IconedLabel } from '@/components/ui/IconedLabel';
import { InfoIconWithTooltip } from '@/components/ui/InfoIconWithTooltip';
import { SectionBox } from '@/components/ui/SectionBox';
import {
  StatusIndicator,
  StatusIndicatorVariant,
} from '@/components/ui/StatusIndicator';
import { TableDrawerEmptyState } from '@/components/ui/TableDrawerEmptyState';
import { UserSurveyCTA } from '@/components/ui/UserSurveyCTA';
import { areaIcons } from '@/constants/areaIcons';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { routes } from '@/constants/routes';
import { uiIcons } from '@/constants/uiIcons';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import { useLocaleStore } from '@/stores/locale';
import { formatDate } from '@/utils/date';
import { pascalToCamelCase } from '@/utils/string';

export const ProjectDetails = () => {
  const { t } = useTranslation('portfolio');
  const [searchParams] = useSearchParams();
  const selectedProjectId = searchParams.get(PROJECT_ID_PARAMETER_NAME);
  const hasNewProjectManagement = useHasNewProjectManagement();

  const { data: features } = useGetConfigurationFeatures();

  const { lang } = useLocaleStore();

  const { data: project, isLoading } = useGetProjectById(selectedProjectId);
  const {
    data: projectDetails,
    isLoading: isLoadingDetails,
    isError: isProjectDetailsError,
  } = useGetProjectDetails(selectedProjectId);

  if (isLoading) {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!project) {
    return <TableDrawerEmptyState text={t('emptyState.SelectProject')} />;
  }
  const {
    LastTimeRegistrationDate,
    Name,
    Number,
    CustomerName,
    ProjectManagerName,
    StatusName,
    HealthStatusName,
    IsInternalProject,
  } = project;

  const isKnownStatus = (PROJECT_STATUSES as readonly string[]).includes(
    StatusName,
  );

  // Convert PascalCase from backend (AtRisk, OnTrack) to camelCase for component (atRisk, onTrack)
  const variant = HealthStatusName
    ? pascalToCamelCase(HealthStatusName)
    : undefined;

  // Get recommendations for this project
  const activeRecommendations = getProjectRecommendations(
    project,
    t,
    hasNewProjectManagement,
  );

  const {
    factor,
    factorColor,
    hoursBudget,
    realizedHours,
    plannedHours,
    revenueBudget,
    realizedRevenue,
    plannedRevenue,
  } = projectDetails ?? {};

  const showRevenueChart =
    revenueBudget !== undefined &&
    realizedRevenue !== undefined &&
    plannedRevenue !== undefined;

  const showHoursChart =
    hoursBudget !== undefined &&
    realizedHours !== undefined &&
    plannedHours !== undefined;

  return (
    <Stack gap={4}>
      <Stack gap={3}>
        <Stack>
          <Stack direction="row" alignItems="center" flexWrap="wrap">
            <Typography component="h2" variant="h3SemiBold" color="primary">
              {Name || t('emptyState.UnnamedProject')}
            </Typography>
            <Typography component="p" variant="label">
              ({Number})
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            // Use sx prop due to this issue:
            // https://github.com/mui/material-ui/issues/47844
            sx={{
              columnGap: 3,
              rowGap: 1,
            }}
            flexWrap="wrap"
          >
            <EntityHoverCard
              entityId={project.CustomerId}
              config={customerHoverCardFields}
            >
              <IconedLabel
                iconName={areaIcons.customers}
                iconColor="secondary"
                typographyProps={{
                  color: IsInternalProject ? 'text.disabled' : 'inherit',
                }}
              >
                {IsInternalProject
                  ? t('columns.IsInternalProject')
                  : CustomerName}
              </IconedLabel>
            </EntityHoverCard>

            {ProjectManagerName && (
              <EntityHoverCard
                entityId={project.ProjectManagerId}
                config={employeeHoverCardFields}
                apiKeyName="projectmanagers"
              >
                <IconedLabel
                  iconName={areaIcons.employees}
                  iconColor="secondary"
                  typographyProps={{
                    color: IsInternalProject ? 'text.disabled' : 'inherit',
                  }}
                >
                  {ProjectManagerName}
                </IconedLabel>
              </EntityHoverCard>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          // Use sx prop due to this issue:
          // https://github.com/mui/material-ui/issues/47844
          sx={{
            columnGap: 3,
            rowGap: 1,
          }}
          flexWrap="wrap"
        >
          <Chip
            variant="outlined"
            color="navy"
            size="small"
            label={
              isKnownStatus
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  t(`enums.${StatusName}` as any)
                : StatusName
            }
            monoText
          />

          {variant && (
            <StatusIndicator variant={variant as StatusIndicatorVariant} />
          )}

          {!isProjectDetailsError && features?.projectFactor && (
            <Factor
              factor={factor ?? 0}
              trend={factorColor}
              isLoading={isLoadingDetails}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <IconedLabel
              iconName={uiIcons.metadata.lastActivity}
              iconColor="secondary"
            >
              {t('details.LastRegistrationLabel')}
            </IconedLabel>
            <Typography variant="labelMono" color="secondary">
              {LastTimeRegistrationDate
                ? formatDate(LastTimeRegistrationDate, lang)
                : t('emptyState.NoRegistrationDate')}
            </Typography>
          </Stack>

          <Button
            component={Link}
            to={
              hasNewProjectManagement
                ? routes.projects.planning(selectedProjectId!)
                : legacyRoutes.projectManagement.planning(selectedProjectId!)
            }
            variant="outlined"
            color="secondary"
            data-automation-id="portfolio-view-project-button"
          >
            {t('details.ViewProjectButton')}
          </Button>
        </Stack>
      </Stack>

      <Divider />
      <Stack gap={2}>
        <Stack direction="row" alignItems="center">
          <Icon name={areaIcons.reports} color="secondary" size="small" />
          <Typography
            component="h2"
            variant="bodyRegularMono"
            color="secondary"
          >
            {t('details.InsightsHeading')}
          </Typography>
          <InfoIconWithTooltip
            id="insights-tooltip"
            tooltip={<Trans i18nKey="details.InsightsTooltip" ns="portfolio" />}
            size="small"
          />
        </Stack>

        <Stack direction="row" gap={4} flexWrap="wrap">
          {isLoadingDetails ? (
            <>
              <div className={styles.ChartWrapper}>
                <GaugeRealizedLoading />
              </div>
              <div className={styles.ChartWrapper}>
                <GaugeRealizedLoading />
              </div>
            </>
          ) : (
            <>
              {showHoursChart && (
                <div className={styles.ChartWrapper}>
                  <GaugeRealized
                    type={'hours'}
                    title={t('details.ProjectHoursTitle')}
                    lang={lang}
                    realizedValue={realizedHours}
                    plannedValue={plannedHours}
                    budgetValue={hoursBudget}
                  />
                </div>
              )}
              {showRevenueChart && (
                <div className={styles.ChartWrapper}>
                  <GaugeRealized
                    type={'currency'}
                    title={t('details.ProjectRevenueTitle')}
                    lang={lang}
                    currency={projectDetails?.currencyCode}
                    realizedValue={realizedRevenue}
                    plannedValue={plannedRevenue}
                    budgetValue={revenueBudget}
                  />
                </div>
              )}
            </>
          )}
        </Stack>
      </Stack>
      {activeRecommendations.length > 0 ? (
        <>
          <Divider />
          <Stack gap={2} id="recommendations-section">
            <Stack direction="row" alignItems="center">
              <Icon
                name={uiIcons.healthStatus.health}
                color="secondary"
                size="small"
              />
              <Typography
                component="h2"
                variant="bodyRegularMono"
                color="secondary"
              >
                {t('details.ProjectHealthHeading')}
              </Typography>
            </Stack>
            {activeRecommendations.map((rec, index) => (
              <SectionBox key={index}>
                <Stack gap={2}>
                  <Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <StatusIndicator iconOnly variant="atRisk" />
                      <Typography variant="label" uppercase>
                        {t('healthStatus.AtRisk', { ns: 'ui' })}
                      </Typography>
                    </Stack>

                    <Typography component="h3" variant="h4">
                      {rec.title}
                    </Typography>
                  </Stack>
                  <Stack>
                    <IconedLabel
                      iconName={uiIcons.controls.forward}
                      iconColor="secondary"
                      typographyProps={{ uppercase: true }}
                    >
                      {t('details.NextStepLabel')}
                    </IconedLabel>
                    <Typography component="p">{rec.description}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      component={Link}
                      to={rec.actionLink}
                      color="secondary"
                      variant="contained"
                      size="small"
                      data-automation-id="portfolio-recommendation-action-button"
                    >
                      {rec.actionLabel}
                    </Button>
                  </Stack>
                </Stack>
              </SectionBox>
            ))}
          </Stack>
        </>
      ) : null}
      <Divider />
      <UserSurveyCTA surveyId="019c56f7-51bb-0000-b51a-4810e70efd9a" />
    </Stack>
  );
};
